import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap, catchError, filter, take } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';
import { Company, UserFullInfo } from 'src/app/user/model/user';
import { ReductionObjective } from '../model/reduction-objective';

export interface CurrentUserInfo {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  companyName: string;
  initials: string;
  email: string;
  phone?: string;
  companyId: string;
  company?: Company;
  preferredLanguage?: string;
  marketingOptOut?: boolean;
  createdAt?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  private http = inject(HttpClient);

  // Signal pour l'état de chargement
  isLoading = signal<boolean>(false);

  // Signal pour l'utilisateur complet
  private userFullInfo = signal<UserFullInfo | null>(null);

  // Observable de l'état de chargement (créé une fois dans le contexte d'injection)
  private isLoading$ = toObservable(this.isLoading);

  // Computed signals pour l'interface utilisateur
  currentUser = computed<CurrentUserInfo | null>(() => {
    const user = this.userFullInfo();
    if (!user) return null;

    return {
      id: user.id || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role || '',
      phone: user.phone || '',
      email: user.emails?.[0].email || '',
      companyName: user.company?.name || '',
      companyId: user.company?.id || '',
      initials: this.generateInitials(user.firstName, user.lastName),
      company: user.company,
      preferredLanguage: user.preferredLanguage,
      marketingOptOut: user.marketingOptOut,
      createdAt: user.createdAt,
    };
  });
  /**
   * The company the user actually belongs to.
   *
   * Distinct from `currentUser().companyId`, which mirrors the *tenant* company
   * returned by `/me` — the impersonated one while a super admin acts as
   * another company. Use this whenever you need the real owner identity
   * (e.g. detecting that an impersonation is active).
   */
  ownCompanyId = computed<string>(() => {
    return this.userFullInfo()?.companyId ?? '';
  });

  pack = computed(() => {
    return this.currentUser()?.company?.pack;
  });

  objectives = computed<ReductionObjective[]>(() => {
    return this.currentUser()?.company?.reductionObjectives ?? [];
  });

  // Computed signal pour déterminer si l'utilisateur est admin (inclut superAdmin)
  isAdmin = computed<boolean>(() => {
    const user = this.userFullInfo();
    return user?.role === 'admin' || user?.role === 'superAdmin';
  });

  // Computed signal pour déterminer si l'utilisateur est super admin
  isSuperAdmin = computed<boolean>(() => {
    const user = this.userFullInfo();
    return user?.role === 'superAdmin';
  });

  // Observable version pour les guards et services qui ont besoin d'un Observable
  private isAdminObservable$ = toObservable(this.isAdmin);
  private isSuperAdminObservable$ = toObservable(this.isSuperAdmin);

  isAdmin$ = (): Observable<boolean> => this.isAdminObservable$;
  isSuperAdmin$ = (): Observable<boolean> => this.isSuperAdminObservable$;

  /**
   * Charge les informations de l'utilisateur connecté
   * Peut être appelé plusieurs fois sans risque (évite les appels multiples)
   * @returns Observable qui émet true quand le chargement est terminé
   */
  loadCurrentUser(): Observable<boolean> {
    // Si déjà chargé, retourner immédiatement
    if (this.userFullInfo()) {
      return of(true);
    }

    // Si déjà en cours de chargement, attendre la fin
    if (this.isLoading()) {
      return this.isLoading$.pipe(
        filter((loading) => !loading),
        take(1),
        map(() => true),
      );
    }

    this.isLoading.set(true);

    return this.http.get<UserFullInfo>(`${environment.apiUrl}/me`).pipe(
      tap((data) => {
        this.userFullInfo.set(data);
        this.isLoading.set(false);
      }),
      map(() => true),
      catchError((err) => {
        console.error('Failed to load current user info:', err);
        this.isLoading.set(false);
        return of(false);
      }),
    );
  }

  /**
   * Force le rechargement des informations utilisateur
   */
  refreshCurrentUser(): void {
    this.userFullInfo.set(null);
    this.loadCurrentUser().subscribe();
  }

  /**
   * Met à jour localement des champs de la company en cache, sans refaire d'appel réseau
   * Utile pour refléter immédiatement le résultat d'une sauvegarde (ex: mise à jour optimiste)
   */
  patchCompany(partial: Partial<Company>): void {
    const current = this.userFullInfo();
    if (!current?.company) return;
    this.userFullInfo.set({
      ...current,
      company: { ...current.company, ...partial },
    });
  }

  /**
   * Nettoie les données utilisateur (utile lors de la déconnexion)
   */
  clearCurrentUser(): void {
    this.userFullInfo.set(null);
  }

  /**
   * Génère les initiales à partir du prénom et nom
   */
  private generateInitials(firstName?: string, lastName?: string): string {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return `${first}${last}` || 'U';
  }
}
